import Panel, { PanelBody, PanelHeader } from '@aurora/shared-client/components/common/Panel/Panel';
import type { PageAndEmptyParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { EndUserComponent, EndUserPages } from '@aurora/shared-types/pages/enums';
import React, { useId } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import useTranslation from '../../useTranslation';
import ForgotPasswordForm from '@aurora/shared-client/components/authentication/ForgotPasswordForm/ForgotPasswordForm';

/**
 * Widget Component for the <code>ForgotPasswordPage</code>.
 *
 * @constructor
 *
 * @author Patricio Poratto, Willi Hyde, Dolan Halbrook
 */
const ForgotPasswordWidget: WidgetFC<WidgetProps> = () => {
  const cx = useClassNameMapper();
  const {
    formatMessage,
    FormattedMessage,
    loading: textLoading
  } = useTranslation(EndUserComponent.FORGOT_PASSWORD_WIDGET);
  const { router, Link } = useEndUserRoutes();
  const uid = useId();

  if (textLoading) {
    return null;
  }

  return (
    <main className={cx('lia-g-auth-wrapper')}>
      <Panel className={cx('lia-g-auth-panel')} data-testid={uid}>
        <PanelHeader className={cx('lia-g-auth-panel-header')}>
          <h1 className={cx('lia-g-auth-panel-h1 lia-g-mb-5')}>{formatMessage('title')}</h1>
          <span className={cx('small text-muted')}>{formatMessage('description')}</span>
        </PanelHeader>
        <PanelBody className={cx('lia-g-auth-panel-body')}>
          <ForgotPasswordForm
            onFormSuccess={() => router.pushRoute<PageAndEmptyParams>(EndUserPages.LoginPage)}
          />
          <div className={cx('lia-g-auth-nav-section')}>
            <FormattedMessage
              id="nav.text.login"
              values={{
                link: function renderChunks(chunks): React.ReactNode {
                  return (
                    <Link<PageAndEmptyParams> route={EndUserPages.LoginPage} legacyBehavior={true}>
                      <a>{chunks}</a>
                    </Link>
                  );
                }
              }}
            />
          </div>
        </PanelBody>
      </Panel>
    </main>
  );
};

export default ForgotPasswordWidget;
